import React, { Fragment, useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import Image from '@atlaskit/image';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { Spotlight } from '@atlaskit/onboarding';
import {
	PopoverContent,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
} from '@atlaskit/spotlight';

import FPSChangeboardingSpotlight from '../../../common/constants/assets/fps-changeboarding-spotlight.svg';
import { SPOTLIGHT_ONBOARDING_STEP1_TARGET } from '../../../common/constants/spotlight';
import { type FireAnalyticsEvent } from '../../../common/types';
import { useAnalytics } from '../../../controllers/analytics';
import { useAvailableProducts } from '../../../controllers/store/bootstrap';
import { useSpotlightState } from '../../../controllers/store/spotlight';

import messages from './messages';

const spotlightAnalyticsPayload = (action: string, attributes: FireAnalyticsEvent['attributes']) =>
	({
		actionSubject: 'rovoChangeboardingSpotlight',
		attributes: {
			additionalAttributes: attributes,
		},
		eventType: 'ui',
		action,
	}) satisfies FireAnalyticsEvent;

/**
 * New spotlight implementation using @atlaskit/spotlight compositional API.
 * Renders inside a PopoverProvider via CustomSpotlightTarget.
 */
export const SearchPageOnboardingSpotlightNew = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [spotlight, { dismiss }] = useSpotlightState();
	const { fireAnalyticsEvent } = useAnalytics();
	const availableProducts = useAvailableProducts();

	const renderSpotlight = spotlight.state === 'visible' && availableProducts.length > 0;

	useEffect(() => {
		if (renderSpotlight) {
			fireAnalyticsEvent(
				spotlightAnalyticsPayload('view spotlight', {
					step: spotlight.activeSpotlight + 1,
				}),
			);
		}
	}, [fireAnalyticsEvent, renderSpotlight, spotlight]);

	if (!renderSpotlight) {
		return null;
	}

	const handleDismiss = () => {
		dismiss();

		fireAnalyticsEvent(
			spotlightAnalyticsPayload('click ok', {
				step: spotlight.activeSpotlight + 1,
			}),
		);
	};

	return (
		<PopoverContent
			isVisible={renderSpotlight}
			placement="left-end"
			shouldDismissOnClickOutside={false}
			dismiss={handleDismiss}
			offset={[0, 4]}
		>
			<SpotlightCard>
				<SpotlightHeader>
					<SpotlightHeadline>{formatMessage(messages.onboardingStep1Heading)}</SpotlightHeadline>
					<SpotlightControls>
						<SpotlightDismissControl onClick={handleDismiss} />
					</SpotlightControls>
				</SpotlightHeader>
				<SpotlightMedia>
					<Image src={FPSChangeboardingSpotlight} alt="" />
				</SpotlightMedia>
				<SpotlightBody>{formatMessage(messages.onboardingStep1Description)}</SpotlightBody>
				<SpotlightFooter>
					<SpotlightActions>
						<SpotlightPrimaryAction onClick={handleDismiss}>
							{formatMessage(messages.gotIt)}
						</SpotlightPrimaryAction>
					</SpotlightActions>
				</SpotlightFooter>
			</SpotlightCard>
		</PopoverContent>
	);
};

/**
 * Legacy spotlight implementation using @atlaskit/onboarding.
 */
export const SearchPageOnboardingSpotlightLegacy = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [spotlight, { dismiss }] = useSpotlightState();
	const { fireAnalyticsEvent } = useAnalytics();
	const availableProducts = useAvailableProducts();

	const renderSpotlight = spotlight.state === 'visible' && availableProducts.length > 0; // Only render the spotlight after available products have been fetched

	useEffect(() => {
		if (renderSpotlight) {
			fireAnalyticsEvent(
				spotlightAnalyticsPayload('view spotlight', {
					step: spotlight.activeSpotlight + 1, // activeSpotlight starts at 0
				}),
			);
		}
	}, [fireAnalyticsEvent, renderSpotlight, spotlight]);

	if (!renderSpotlight) {
		return null;
	}

	const renderActiveSpotlight = () => {
		const spotlights = [
			// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
			<Spotlight
				actions={[
					{
						onClick: () => {
							dismiss();

							fireAnalyticsEvent(
								spotlightAnalyticsPayload('click ok', {
									step: spotlight.activeSpotlight + 1, // activeSpotlight starts at 0
								}),
							);
						},
						text: formatMessage(messages.ok),
						appearance: 'subtle',
					},
				]}
				heading={formatMessage(messages.onboardingStep1Heading)}
				target={SPOTLIGHT_ONBOARDING_STEP1_TARGET}
				key={SPOTLIGHT_ONBOARDING_STEP1_TARGET}
				dialogWidth={275}
				dialogPlacement="left top"
				targetRadius={8}
			>
				{formatMessage(messages.onboardingStep1Description)}
			</Spotlight>,
		];

		return spotlights[spotlight.activeSpotlight];
	};

	return <Fragment>{renderActiveSpotlight()}</Fragment>;
};
